import React from 'react';
import styled from 'styled-components';
import { Button, ButtonIcon, Cell, Row, Text, TooltipIcon, TooltipWrapper, User } from 'components';
import { DEFAULT_ORG_ADMIN_JOB_TITLE } from 'config/const';
import { theme, toRem } from 'design';
import { EmployeeType, IUploadPayment } from 'types';
import { centsToDollarsDisplay, formatJobTitle } from 'utils';

interface IUploadPaymentSelectorProps {
  payment: IUploadPayment;
  isEmployeeIdUpload?: boolean;
  disabled?: boolean;
  onSetEmployee(uploadPayment: IUploadPayment): void;
  onRemove(): void;
  tooltipId: string;
}

const StyledRow = styled(Row)`
  z-index: unset;

  @media (min-width: ${theme.breakpoint.small}px) {
    &:hover {
      cursor: pointer;
      .select-imported-item-button,
      .remove-imported-item-button {
        visibility: visible;
      }
    }

    .select-imported-item-button,
    .remove-imported-item-button {
      visibility: hidden;
    }
  }
`;

const EmployeeDetails = styled.div`
  display: flex;
  align-items: center;
  min-width: ${toRem(100)};
`;

const StyledText = styled(Text)`
  margin-left: ${toRem(20)};

  &.employee-mismatch-name {
    word-break: break-word;
    overflow-wrap: anywhere;
    white-space: pre-wrap;
    width: 75%;
  }
`;

const ActionButtons = styled.div`
  display: flex;
  flex-flow: row nowrap;
  flex-grow: 1;
  justify-content: space-evenly;
  gap: ${toRem(4)};
`;

const UploadPaymentSelector: React.FC<IUploadPaymentSelectorProps> = ({ payment, onRemove, onSetEmployee, isEmployeeIdUpload = false, disabled, tooltipId }) => {
  if (payment.employee) {
    return (
      <StyledRow isActive={disabled} data-cy="upload-payment-row">
        <Cell>
          <EmployeeDetails>
            <TooltipWrapper>
              {disabled ? (
                <TooltipIcon icon="Error" iconMarginLeft={0} iconMarginRight={12} iconSize={20} id={tooltipId}>
                  You are not allowed to issue tips to this user.
                </TooltipIcon>
              ) : (
                <TooltipIcon
                  icon="Success"
                  iconFill={{
                    default: theme.color.success.medium,
                  }}
                  iconMarginLeft={0}
                  iconMarginRight={12}
                  iconSize={20}
                  id={tooltipId}
                >
                  User matched
                </TooltipIcon>
              )}
            </TooltipWrapper>
            <User
              stackable={false}
              id={payment.employee.id}
              username={payment.employee.name}
              image={payment.employee.user?.avatarUrl}
              hasPaymentMethod={!!payment.employee.user?.card}
              wrapText
              role={formatJobTitle(payment.employee.type === EmployeeType.ADMIN ? DEFAULT_ORG_ADMIN_JOB_TITLE : payment.employee.sites[0]?.job.title)}
            />
          </EmployeeDetails>
        </Cell>
        <Cell>{centsToDollarsDisplay(payment.amount)}</Cell>
        <Cell align="center">
          <ActionButtons>
            <Button className="select-imported-item-button" data-cy="select-imported-item-button" size="small" appearance="primary" label="Select Match" onClick={() => onSetEmployee(payment)} />
            <ButtonIcon className="remove-imported-item-button" data-cy="remove-imported-item-button" label="Delete import" icon="RemoveThick" appearance="destructive" size="small" onClick={onRemove} />
          </ActionButtons>
        </Cell>
      </StyledRow>
    );
  }

  return (
    <StyledRow data-cy="upload-payment-row">
      <Cell maxWidth={400}>
        <EmployeeDetails>
          <TooltipWrapper>
            <TooltipIcon
              icon="Error"
              iconFill={{
                default: payment.employeeMatch && payment.employeeMatch.length > 1 ? theme.color.warning.medium : theme.color.danger.medium,
              }}
              iconMarginRight={12}
              iconMarginLeft={0}
              iconSize={20}
              id={tooltipId}
            >
              {payment.employeeMatch && payment.employeeMatch.length > 1 ? 'Multiple user matches found' : 'No user matches found'}
            </TooltipIcon>
          </TooltipWrapper>
          {isEmployeeIdUpload ? (
            <StyledText size="small" className="employee-mismatch-name">
              {payment.employeeId}
            </StyledText>
          ) : (
            <User stackable={false} id="employee-avatar" username={payment.name} wrapText />
          )}
        </EmployeeDetails>
      </Cell>
      <Cell>{centsToDollarsDisplay(payment.amount)}</Cell>
      <Cell>
        <ActionButtons>
          <Button className="select-imported-item-button" data-cy="select-imported-item-button" size="small" appearance="primary" label="Select Match" onClick={() => onSetEmployee(payment)} />
          <ButtonIcon className="remove-imported-item-button" data-cy="remove-imported-item-button" label="Delete import" icon="RemoveThick" appearance="destructive" size="small" onClick={onRemove} />
        </ActionButtons>
      </Cell>
    </StyledRow>
  );
};

export default UploadPaymentSelector;
